import { getParamValueFromUrl } from 'root/utils/URLParams/URLParams';
import { LoggingHandler } from 'root/utils/errorReporting/LoggingHandler';
import doFetch from 'root/utils/cors/doFetch';

const hiddenFormClass = 'js-hidden';
let renderRecaptchaCheckbox;

const loggingHandlerDetailsObj = {
    tags: ['recaptcha'],
    fileName: 'recaptcha.js',
};

function waitForLoadGrecaptcha(form, maxAttempts = 50, delay = 500) {
    return new Promise(resolve => {
        let attempts = 0;
        function checkGrecaptcha() {
            if (window.grecaptcha) {
                resolve(window.grecaptcha);
            } else if (attempts >= maxAttempts) {
                LoggingHandler().logError('The window.recaptcha object was not loaded', {
                    searchTerm: 'grecapthca-not-loaded',
                    tags: ['recaptcha'],
                    fileName: 'recaptcha.js',
                    additionalData: { numOfAttempts: attempts },
                });
                form.classList.remove(hiddenFormClass);
            } else {
                attempts++;
                setTimeout(checkGrecaptcha, delay);
            }
        }
        checkGrecaptcha();
    });
}

const loadCaptcha = (form, actionName) => {
    waitForLoadGrecaptcha(form).then(grecaptcha => {
        grecaptcha.enterprise.ready(function () {
            const formEls = document.querySelectorAll(
                '[data-form="ls-form"], [data-form="ls-form-steps"]'
            );
            const recaptchaAPIArray = Array.from(document.scripts)
                .filter(el => el.src.includes('recaptcha/enterprise'))
                .map(el =>
                    el.src.split('?render=')
                )[0]; /* the key (based on env) is already defined in the script tag in the DOM*/
            const recaptchaSiteKey =
                recaptchaAPIArray.length > 1 ? recaptchaAPIArray[1] : '';
            if (formEls.length > 0 && recaptchaSiteKey) {
                window.grecaptcha.enterprise
                    .execute(recaptchaSiteKey, {
                        action: actionName,
                    })
                    .then(token => {
                        recaptchaAPICall(form, token);
                    });
            }
        });
    });
};

const loadCheckboxCaptcha = form => {
    const recaptchaContainer = form.querySelector('[data-form="recaptcha-container"]');
    if (recaptchaContainer && window.grecaptcha) {
        const recaptchaKeyHiddenField = form.querySelector(
            'input[name=recaptcha_site_key]'
        );
        const recaptchaCheckboxSiteKey =
            recaptchaKeyHiddenField && recaptchaKeyHiddenField.value;
        if (recaptchaCheckboxSiteKey) {
            const verifyCallback = function (response) {
                if (response) {
                    const recaptchaFieldError = form.querySelector(
                        '[data-formfield-error=recaptcha]'
                    );
                    recaptchaFieldError.classList.remove('js-label-message-error');
                    loggingHandlerDetailsObj.searchTerm = 'recaptcha-solved';
                    LoggingHandler().logUserAction(
                        'User solved the recaptcha',
                        loggingHandlerDetailsObj
                    );
                } else {
                    loggingHandlerDetailsObj.searchTerm = 'recaptcha-not-solved';
                    LoggingHandler().logUserAction(
                        'User did not solve the recaptcha',
                        loggingHandlerDetailsObj
                    );
                }
            };

            const recaptcha = window.grecaptcha.enterprise;

            recaptcha &&
                recaptcha.ready(function () {
                    renderRecaptchaCheckbox = recaptcha.render(recaptchaContainer, {
                        sitekey: recaptchaCheckboxSiteKey,
                        callback: verifyCallback,
                    });
                    form.classList.remove(hiddenFormClass);
                });
        }
    }
};

const recaptchaFormFieldValidation = (form, event = null) => {
    const currentTargetIsNextButton =
        event && event.target?.classList.contains('js-cta-next');
    if (!currentTargetIsNextButton) {
        const recaptcha = window.grecaptcha.enterprise ?? null;
        const recaptchaResponse =
            recaptcha && recaptcha.getResponse(renderRecaptchaCheckbox);
        const recaptchaEl = form.querySelector('[data-form=recaptcha]');
        const errorMessage =
            recaptchaEl && recaptchaEl.querySelector('[data-formfield-error=recaptcha]');
        const showErrorMsgClass = 'js-label-message-error';
        const recaptchaField = form.querySelector('#g-recaptcha-response');

        if (recaptchaResponse === '') {
            // When the user solves the challenge successfully, the recaptcha getResponse function returns a token
            // as a string. In case of unsuccessful resolution, it returns an empty string. If this is the case,
            // we prevent the user from submitting the form
            event && event.preventDefault();
            showErrorMsgClass &&
                errorMessage &&
                errorMessage.classList.add(showErrorMsgClass);
            if (recaptchaField) {
                recaptchaField.setCustomValidity('invalid');
            }
        } else {
            showErrorMsgClass &&
                errorMessage &&
                errorMessage.classList.remove(showErrorMsgClass);
            if (recaptchaField) {
                recaptchaField.setCustomValidity('');
            }
        }
    }
};

const getRequestOptions = token => {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/x-www-form-urlencoded');

    const urlencoded = new URLSearchParams();
    urlencoded.append('token', token);

    return {
        method: 'POST',
        headers: myHeaders,
        body: urlencoded,
        cors: 'no-cors',
        redirect: 'follow',
    };
};

const setCheckboxVisibilityBasedOnResponse = (data, form) => {
    const isValidRecaptchaScore = data['is_valid_recaptcha_score']
        ? data['is_valid_recaptcha_score']
        : false;
    if (isValidRecaptchaScore !== true || getParamValueFromUrl('debug_recaptcha')) {
        loadCheckboxCaptcha(form);
    } else {
        form.classList.remove(hiddenFormClass);
        const recaptchaContainer = form.querySelector('[data-form=recaptcha]');
        if (recaptchaContainer) recaptchaContainer.style.display = 'none';
    }
};

const recaptchaAPICall = async (form, token) => {
    const endpoint = '/rest/forms/load/recaptcha-validation';
    loggingHandlerDetailsObj.searchTerm = 'recaptcha-api-error';
    return await doFetch(
        endpoint,
        loggingHandlerDetailsObj,
        setCheckboxVisibilityBasedOnResponse,
        loadCheckboxCaptcha,
        getRequestOptions(token),
        [form]
    );
};

export { loadCaptcha, recaptchaFormFieldValidation };
